"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search, TrendingUp, MapPin, Users, Target, CircleCheck as CheckCircle2, ArrowRight, ChartBar as BarChart3, Sparkles, Compass, ChartPie as PieChart } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const landscapeStats = [{
  icon: Search,
  value: "68%",
  label: "Commercial Local Intent",
  description: "Of Cheltenham searches carry direct commercial or transactional intent for local professional services.",
  highlight: "High Conversion Intent"
}, {
  icon: Users,
  value: "74%",
  label: "Mobile Micro-Moments",
  description: "Local customers searching on smartphones while actively seeking services, dining, or consultancy in town.",
  highlight: "Mobile-First Opportunity"
}, {
  icon: MapPin,
  value: "52%",
  label: "Map Pack & AI Search",
  description: "Local discovery happens directly within Google Maps and AI Overviews before a user clicks a website.",
  highlight: "Zero-Click Visibility"
}, {
  icon: Target,
  value: "60%+",
  label: "SEO Market Gap",
  description: "Local Gloucestershire businesses still rely on outdated SEO, leaving high-value rankings accessible.",
  highlight: "Immediate Growth Margin"
}];
const customerBehaviors = [{
  title: "High-Intent Executive & B2B Search",
  subtitle: "Cheltenham's thriving tech, financial, and legal sectors drive high-value queries.",
  points: ["Decision-makers search for bespoke local advisory services with geo-targeted keywords.", "Emphasis on trust signals, direct consultant expertise, and clear proof of performance.", "High willingness to convert when presented with clear technical authority."],
  category: "B2B & Professional"
}, {
  title: "Premium Consumer & Retail Intent",
  subtitle: "Affluent local demographics seeking premium retail, hospitality, and specialized health services.",
  points: ["Strong demand for bespoke, luxury, and boutique services within the GL50-GL53 postcodes.", "High engagement with Google Business Profiles, authentic reviews, and local content.", "Fast response expectations with frictionless mobile experience."],
  category: "B2C & Lifestyle"
}, {
  title: "AI-Powered Search & Local Discovery",
  subtitle: "How modern Cheltenham consumers interact with Google Gemini and ChatGPT search.",
  points: ["Natural language queries like 'Who is the top SEO consultant in Cheltenham for small business?'", "Direct recommendation requests favoring brands with structured, authoritative entity presence.", "AI Overviews pulling real-time context from geo-targeted landing pages and citations."],
  category: "AI & Future Search"
}];
const marketOpportunityPoints = [{
  title: "Direct Consultant Access",
  desc: "You work directly with me—no account managers, no junior staff, and no generic agency templates."
}, {
  title: "Hyper-Targeted Gloucestershire Strategy",
  desc: "Tailored keyword frameworks capturing Cheltenham, Gloucester, Stroud, and Cotswold search volume."
}, {
  title: "Technical & Entity Optimisation",
  desc: "Structured data and semantic content built for both Google's traditional ranker and AI search engines."
}, {
  title: "Transparent & Measurable Lead Growth",
  desc: "Focusing on qualified inquiries, organic phone calls, and high-value customer acquisitions."
}];
export default function Cheltenham() {
  const [activeTab, setActiveTab] = useState(0);
  return <section id="cheltenhammarketlandscape" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-secondary/60 rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-50 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide uppercase border border-accent/30 text-foreground">
            <MapPin className="size-3.5 text-accent mr-1 inline" />
            Cheltenham & Gloucestershire Search Market
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-primary tracking-tight mb-6">
            Cheltenham Local Search Landscape & Market Opportunity
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            As an independent freelance SEO consultant, I analyze real search patterns in Cheltenham to help businesses outrank competitors and capture high-intent local market demand.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {landscapeStats.map((stat, i) => {
          const IconComponent = stat.icon;
          return <motion.div key={stat.label} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full border border-border/80 bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-300 hover:shadow-md flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="p-2.5 rounded-lg bg-secondary text-primary">
                        <IconComponent className="size-5 text-primary" />
                      </div>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent/15 text-accent-foreground border border-accent/20">
                        {stat.highlight}
                      </span>
                    </div>
                    <CardTitle className="text-3xl sm:text-4xl font-serif font-bold text-primary tracking-tight">
                      {stat.value}
                    </CardTitle>
                    <CardDescription className="text-foreground font-semibold text-base mt-1">
                      {stat.label}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {stat.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="bg-card rounded-2xl border border-border/80 shadow-sm p-6 sm:p-10 mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <Badge variant="outline" className="mb-3 border-primary/30 text-primary">
                <BarChart3 className="size-3.5 mr-1 text-primary" />
                Customer Search Dynamics
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-serif font-bold text-primary mb-4">
                How Cheltenham Customers Search Online
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                Understanding Cheltenham's unique demographic and commercial makeup is crucial. Businesses here compete not just on keyword volume, but on search intent, trust, and proximity.
              </p>

              <div className="space-y-3">
                {customerBehaviors.map((item, i) => <div key={item.category} data-index={i}>
                    <Button variant={activeTab === i ? "default" : "outline"} className={`w-full justify-start text-left h-auto py-3 px-4 font-medium transition-all ${activeTab === i ? "bg-primary text-primary-foreground shadow-xs" : "bg-background text-foreground hover:bg-secondary/60"}`} onClick={() => setActiveTab(i)}>
                      <Compass className={`size-4 mr-2.5 shrink-0 ${activeTab === i ? "text-primary-foreground" : "text-accent"}`} />
                      <div>
                        <div className="text-sm font-semibold">{item.category}</div>
                        <div className={`text-xs ${activeTab === i ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                          {item.title}
                        </div>
                      </div>
                    </Button>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-7 bg-secondary/40 rounded-xl p-6 sm:p-8 border border-border/60">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-accent-foreground bg-accent/20 px-2.5 py-1 rounded-md border border-accent/30">
                  {customerBehaviors[activeTab].category}
                </span>
                <span className="text-xs text-muted-foreground flex items-center">
                  <Sparkles className="size-3.5 text-accent mr-1" /> Verified Local Pattern
                </span>
              </div>

              <h4 className="text-xl sm:text-2xl font-serif font-semibold text-primary mb-2">
                {customerBehaviors[activeTab].title}
              </h4>
              <p className="text-muted-foreground text-sm mb-6">
                {customerBehaviors[activeTab].subtitle}
              </p>

              <Separator className="mb-6 bg-border/60" />

              <div className="space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Key Intent Characteristics:
                </div>
                {customerBehaviors[activeTab].points.map((point, idx) => <div key={idx} data-index={idx} className="flex items-start gap-3">
                    <div className="mt-0.5 p-1 rounded-full bg-primary/10 text-primary shrink-0">
                      <CheckCircle2 className="size-4 text-primary" />
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">
                      {point}
                    </p>
                  </div>)}
              </div>

              <div className="mt-8 p-4 rounded-lg bg-secondary border-l-4 border-accent text-card-foreground">
                <p className="text-xs sm:text-sm font-medium text-foreground">
                  <strong className="text-accent font-semibold">Strategic Takeaway:</strong> To rank in Cheltenham, your SEO strategy must align localized landing pages with real user queries—not generic national templates.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20">
          <div className="lg:col-span-6 space-y-6">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold text-foreground border border-accent/20">
              <PieChart className="size-3.5 text-accent mr-1 inline" />
              Tailored Local SEO Strategy
            </Badge>
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-primary">
              Capitalize on Cheltenham's High-Value Search Volume
            </h3>
            <p className="text-muted-foreground text-base leading-relaxed">
              When you partner with me, you work directly with a seasoned freelance SEO consultant who takes full personal responsibility for your organic search growth in Cheltenham and across the UK.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {marketOpportunityPoints.map((item, idx) => <div key={item.title} data-index={idx} className="p-4 rounded-xl bg-card border border-border/80 shadow-2xs">
                  <h4 className="text-sm font-bold text-primary mb-1 flex items-center gap-1.5">
                    <span className="size-2 rounded-full bg-accent inline-block" />
                    {item.title}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-card via-secondary/30 to-background border border-border shadow-lg">
              <div className="flex items-center justify-between pb-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-base font-serif">
                    YE
                  </div>
                  <div>
                    <div className="text-sm font-bold text-foreground">Yassine Essaadi</div>
                    <div className="text-xs text-muted-foreground">Independent Freelance SEO Consultant</div>
                  </div>
                </div>
                <Badge variant="outline" className="border-accent/40 text-accent font-medium text-xs">
                  Direct Expert Access
                </Badge>
              </div>

              <div className="py-6 space-y-4">
                <div className="p-4 rounded-lg bg-background border border-border/80 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-secondary text-primary">
                      <TrendingUp className="size-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Cheltenham SEO Opportunity</div>
                      <div className="text-sm font-bold text-primary">High-Intent Organic Traffic</div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-accent bg-accent/10 px-2.5 py-1 rounded-full">
                    Targeted
                  </span>
                </div>

                <div className="p-4 rounded-lg bg-background border border-border/80 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-secondary text-primary">
                      <Search className="size-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">AI & Local Map Visibility</div>
                      <div className="text-sm font-bold text-primary">Map Pack + AI Overviews</div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                    Modern SEO
                  </span>
                </div>
              </div>

              <div className="pt-2 text-center">
                <p className="text-xs text-muted-foreground italic mb-4">
                  "No agency bloat. No middle management. Just targeted, high-converting search visibility."
                </p>
                <Button size="sm" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" asChild>
                  <Link to="/LocalSEOPage">
                    Explore My Local SEO Services
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 text-center relative overflow-hidden shadow-xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
          <div className="relative z-10 max-w-3xl mx-auto space-y-6">
            <Badge className="bg-accent text-accent-foreground border-none px-3 py-1 text-xs font-semibold">
              Free Consultation
            </Badge>
            <h3 className="text-xl sm:text-2xl font-serif font-bold tracking-tight">
              Ready to Capture Cheltenham Search Volume?
            </h3>
            <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
              Book a free 30-minute consultation directly with me. We will review your current search visibility, analyze your Cheltenham competitors, and map out a tailored strategy.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 w-full sm:w-auto font-semibold px-8 shadow-md" asChild>
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="border-primary-foreground/30 text-foreground bg-background hover:bg-secondary w-full sm:w-auto font-semibold" asChild>
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}